import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileText, 
  AlertTriangle, 
  Calendar, 
  Download, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  FileSpreadsheet, 
  RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('pnl'); // 'pnl' or 'overdue'

  // --- Overdue Accounts State ---
  const [overdueAccounts, setOverdueAccounts] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  // --- P&L Report State ---
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const defaultStartDate = thirtyDaysAgo.toISOString().split('T')[0];
  const defaultEndDate = today.toISOString().split('T')[0];

  const [pnlStartDate, setPnlStartDate] = useState(defaultStartDate);
  const [pnlEndDate, setPnlEndDate] = useState(defaultEndDate);
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);

  useEffect(() => {
    fetchOverdueAccounts();
    fetchPnLReport();
  }, []);

  useEffect(() => {
    fetchOverdueAccounts();
  }, [days]);

  const fetchOverdueAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/overdue-accounts?days=${days}`);
      setOverdueAccounts(response.data);
    } catch (error) {
      toast.error('Error al cargar reporte de cuentas vencidas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPnLReport = async () => {
    setPnlLoading(true);
    try {
      const response = await axios.get(`${API}/reports/pnl?start_date=${pnlStartDate}T00:00:00Z&end_date=${pnlEndDate}T23:59:59Z`);
      setPnlData(response.data);
    } catch (error) {
      toast.error('Error al cargar reporte de pérdidas y ganancias');
    } finally {
      setPnlLoading(false);
    }
  };

  const handleDownloadReport = async (format) => {
    try {
      toast.info(`Generando reporte ${format.toUpperCase()}...`);
      const response = await axios.get(
        `${API}/reports/pnl/download?format=${format}&start_date=${pnlStartDate}T00:00:00Z&end_date=${pnlEndDate}T23:59:59Z`,
        { responseType: 'blob' }
      );
      
      const fileType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const fileExtension = format === 'pdf' ? 'pdf' : 'xlsx';
      
      const blob = new Blob([response.data], { type: fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pnl_report_${pnlStartDate}_to_${pnlEndDate}.${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success(`Reporte ${format.toUpperCase()} descargado con éxito`);
    } catch (error) {
      console.error(error);
      toast.error('Error al descargar el reporte');
    }
  };

  const totalOverdue = overdueAccounts.reduce((sum, acc) => sum + acc.balance, 0);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      case 'medium':
        return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
      default:
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-indigo-650 dark:bg-indigo-600 rounded-2xl shadow-indigo-100 dark:shadow-none shadow-md text-white">
            <FileText className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Centro de Reportes</h1>
            <p className="text-muted-foreground mt-1">
              Informes financieros consolidados de la PYME
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-border pb-2">
        <Button
          variant={activeTab === 'pnl' ? 'default' : 'outline'}
          onClick={() => setActiveTab('pnl')}
          className="rounded-xl font-medium"
        >
          Estado de Resultados (P&L)
        </Button>
        <Button
          variant={activeTab === 'overdue' ? 'default' : 'outline'}
          onClick={() => setActiveTab('overdue')}
          className="rounded-xl font-medium"
        >
          Cuentas Vencidas
        </Button>
      </div>

      {/* TAB 1: P&L REPORT */}
      {activeTab === 'pnl' && (
        <div className="space-y-6">
          {/* Filters Card */}
          <Card>
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-end gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                  <div className="space-y-2">
                    <Label htmlFor="pnl-start-date" className="text-xs uppercase tracking-wider text-muted-foreground">Fecha Inicio</Label>
                    <Input
                      id="pnl-start-date"
                      type="date"
                      value={pnlStartDate}
                      onChange={(e) => setPnlStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pnl-end-date" className="text-xs uppercase tracking-wider text-muted-foreground">Fecha Término</Label>
                    <Input
                      id="pnl-end-date"
                      type="date"
                      value={pnlEndDate}
                      onChange={(e) => setPnlEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={fetchPnLReport} disabled={pnlLoading} className="rounded-xl">
                    <RefreshCw className={`h-4 w-4 mr-2 ${pnlLoading ? 'animate-spin' : ''}`} />
                    {pnlLoading ? 'Generando...' : 'Generar P&L'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {pnlLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground animate-pulse">Generando Balance P&L...</div>
            </div>
          ) : pnlData ? (
            <>
              {/* Financial Summary KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Ventas Netas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
                      {formatCLP(pnlData.net_sales)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Costo de Ventas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold font-mono text-red-655 dark:text-red-400">
                      -{formatCLP(pnlData.cost_of_sales)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Utilidad Bruta
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold font-mono text-emerald-650 dark:text-emerald-400">
                      {formatCLP(pnlData.gross_profit)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Total Gastos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold font-mono text-purple-650 dark:text-purple-400">
                      -{formatCLP(pnlData.total_expenses)}
                    </div>
                  </CardContent>
                </Card>

                <Card className={pnlData.net_profit >= 0 ? "bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-200" : "bg-red-50/50 dark:bg-red-950/10 border-red-200"}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                      Utilidad Neta
                      {pnlData.net_profit >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-xl font-extrabold font-mono ${pnlData.net_profit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                      {formatCLP(pnlData.net_profit)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Breakdown Table */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2 shadow-sm">
                  <CardHeader className="border-b border-border bg-muted/40">
                    <CardTitle className="text-lg font-bold">Estado de Resultados Consolidado</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-sm text-left border-collapse">
                      <thead>
                        <tr className="bg-muted/30 border-b border-border">
                          <th className="px-6 py-3.5 font-semibold text-muted-foreground uppercase tracking-wider">Concepto Contable</th>
                          <th className="px-6 py-3.5 font-semibold text-muted-foreground text-right uppercase tracking-wider">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border font-medium">
                        {/* Ingresos */}
                        <tr>
                          <td className="px-6 py-3">Ingresos Operacionales (Ventas Brutas)</td>
                          <td className="px-6 py-3 text-right font-mono">{formatCLP(pnlData.gross_sales)}</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-3 text-muted-foreground">(-) Descuentos Otorgados</td>
                          <td className="px-6 py-3 text-right font-mono text-red-600">-{formatCLP(pnlData.discount_total)}</td>
                        </tr>
                        <tr className="bg-indigo-50/30 dark:bg-indigo-950/10 font-bold">
                          <td className="px-6 py-3.5 text-indigo-850 dark:text-indigo-400">Ventas Netas</td>
                          <td className="px-6 py-3.5 text-right font-mono text-indigo-700 dark:text-indigo-400">{formatCLP(pnlData.net_sales)}</td>
                        </tr>
                        {/* Costos */}
                        <tr>
                          <td className="px-6 py-3 text-muted-foreground">(-) Costo de Ventas (COGS)</td>
                          <td className="px-6 py-3 text-right font-mono text-red-600">-{formatCLP(pnlData.cost_of_sales)}</td>
                        </tr>
                        <tr className="bg-emerald-50/20 dark:bg-emerald-950/5 font-extrabold text-base">
                          <td className="px-6 py-3.5">UTILIDAD BRUTA</td>
                          <td className="px-6 py-3.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{formatCLP(pnlData.gross_profit)}</td>
                        </tr>
                        {/* Gastos */}
                        <tr className="bg-muted/10">
                          <td className="px-6 py-3.5 font-bold" colSpan="2">Gastos Operacionales (Egresos)</td>
                        </tr>
                        {Object.entries(pnlData.expenses_by_category).length === 0 ? (
                          <tr>
                            <td className="px-6 py-3 text-muted-foreground italic pl-10" colSpan="2">Sin egresos registrados en este período</td>
                          </tr>
                        ) : (
                          Object.entries(pnlData.expenses_by_category).map(([cat, amt]) => (
                            <tr key={cat}>
                              <td className="px-6 py-2.5 pl-10 text-muted-foreground">Gastos en {cat}</td>
                              <td className="px-6 py-2.5 text-right font-mono">-{formatCLP(amt)}</td>
                            </tr>
                          ))
                        )}
                        <tr className="bg-purple-50/10 dark:bg-purple-950/5 font-bold">
                          <td className="px-6 py-3">Total Gastos Operacionales</td>
                          <td className="px-6 py-3 text-right font-mono text-purple-700 dark:text-purple-400">-{formatCLP(pnlData.total_expenses)}</td>
                        </tr>
                        {/* Resultado Final */}
                        <tr className={`font-extrabold text-lg ${pnlData.net_profit >= 0 ? "bg-emerald-100/50 dark:bg-emerald-950/20" : "bg-red-100/50 dark:bg-red-950/20"}`}>
                          <td className={pnlData.net_profit >= 0 ? "px-6 py-4 text-emerald-800 dark:text-emerald-400" : "px-6 py-4 text-red-800 dark:text-red-400"}>
                            UTILIDAD NETA DEL EJERCICIO
                          </td>
                          <td className={`px-6 py-4 text-right font-mono ${pnlData.net_profit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                            {formatCLP(pnlData.net_profit)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Download Panel */}
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-md font-bold">Exportar Declaración Financiera</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button 
                        onClick={() => handleDownloadReport('pdf')} 
                        className="w-full justify-start rounded-xl"
                        variant="outline"
                      >
                        <FileText className="h-4 w-4 mr-2.5 text-red-500" />
                        Descargar Reporte en PDF (P&L)
                      </Button>
                      <Button 
                        onClick={() => handleDownloadReport('excel')} 
                        className="w-full justify-start rounded-xl"
                        variant="outline"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2.5 text-green-600" />
                        Descargar Reporte en Excel (XLSX)
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="bg-muted/40">
                    <CardContent className="p-4 space-y-2 text-xs text-muted-foreground leading-relaxed">
                      <p className="font-semibold text-foreground text-sm mb-1.5 flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-1.5 text-amber-500" />
                        Nota de Contabilidad:
                      </p>
                      <p>
                        Este reporte extrae los impuestos (IVA de 19% u otro configurado) para mostrar las utilidades reales sobre valores netos si el flag de impuestos está activo.
                      </p>
                      <p>
                        Los costos de venta se calculan en base al costo histórico de adquisición guardado al momento de cada venta.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Genera un reporte seleccionando un rango de fechas válido arriba.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* TAB 2: OVERDUE ACCOUNTS */}
      {activeTab === 'overdue' && (
        <div className="space-y-6">
          {/* Filter */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-end space-x-4">
                <div className="space-y-2 flex-1">
                  <Label>Días sin pago</Label>
                  <Input
                    type="number"
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value))}
                    min="1"
                    data-testid="days-filter-input"
                  />
                </div>
                <Button onClick={fetchOverdueAccounts} disabled={loading} className="rounded-xl">
                  <Calendar className="h-4 w-4 mr-2" />
                  {loading ? 'Cargando...' : 'Actualizar'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-200 dark:border-red-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Vencido
                </CardTitle>
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono text-red-600 dark:text-red-400">
                  {formatCLP(totalOverdue)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Cuentas Vencidas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{overdueAccounts.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Promedio por Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">
                  {overdueAccounts.length > 0
                    ? formatCLP(totalOverdue / overdueAccounts.length)
                    : formatCLP(0)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Overdue Accounts List */}
          <div className="space-y-4">
            {overdueAccounts.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay cuentas vencidas en los últimos {days} días</p>
                </CardContent>
              </Card>
            ) : (
              overdueAccounts.map((account) => (
                <Card
                  key={account.id}
                  className={`hover:shadow-lg transition-shadow border-2 ${getPriorityColor(account.priority)}`}
                  data-testid={`overdue-account-${account.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <AlertTriangle
                            className={`h-6 w-6 ${
                              account.priority === 'high'
                                ? 'text-red-600'
                                : account.priority === 'medium'
                                ? 'text-orange-600'
                                : 'text-yellow-600'
                            }`}
                          />
                          <div>
                            <h3 className="font-semibold text-xl">
                              {account.customer?.name || 'Cliente desconocido'}
                            </h3>
                            {account.customer?.rut && (
                              <p className="text-sm text-muted-foreground font-mono">
                                RUT: {account.customer.rut}
                              </p>
                            )}
                          </div>
                          <span
                            className={`px-3 py-1 text-xs rounded-full font-medium ${
                              account.priority === 'high'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : account.priority === 'medium'
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}
                          >
                            {account.priority === 'high'
                              ? 'Prioridad Alta'
                              : account.priority === 'medium'
                              ? 'Prioridad Media'
                              : 'Prioridad Baja'}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Saldo Pendiente</p>
                            <p className="text-2xl font-bold font-mono text-red-600 dark:text-red-400">
                              {formatCLP(account.balance)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Días Vencidos</p>
                            <p className="text-2xl font-bold">{account.days_overdue}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Transacciones</p>
                            <p className="text-2xl font-bold">{account.transactions?.length || 0}</p>
                          </div>
                        </div>

                        <div className="bg-muted/50 rounded-lg p-3">
                          <p className="text-xs font-semibold mb-2">Último Pago:</p>
                          <p className="text-sm">
                            {account.last_payment_date
                              ? new Date(account.last_payment_date).toLocaleDateString('es-CL', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'Sin pagos registrados'}
                          </p>
                        </div>
                      </div>

                      <div className="ml-6 text-right">
                        {account.customer?.phone && (
                          <div className="mb-2">
                            <p className="text-xs text-muted-foreground">Contacto</p>
                            <p className="text-sm font-medium">{account.customer.phone}</p>
                          </div>
                        )}
                        {account.customer?.email && (
                          <p className="text-xs text-muted-foreground">{account.customer.email}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}